import { CognitoIdentityProviderClient, InitiateAuthCommand } from "@aws-sdk/client-cognito-identity-provider";

const ADMIN_AUTH_MODE = (process.env.REACT_APP_ADMIN_AUTH_MODE || "token").trim().toLowerCase();
const COGNITO_SESSION_STORAGE_KEY = "panetteria_admin_cognito_session_v1";
const SESSION_EXPIRY_SKEW_MS = 30_000;

let cachedCognitoClient;
let cachedCognitoRegion = "";

function getCognitoConfig() {
  const region = (process.env.REACT_APP_COGNITO_REGION || "").trim();
  const userPoolId = (process.env.REACT_APP_COGNITO_USER_POOL_ID || "").trim();
  const clientId = (process.env.REACT_APP_COGNITO_CLIENT_ID || "").trim();

  if (!region || !userPoolId || !clientId) {
    return undefined;
  }

  return {
    region,
    userPoolId,
    clientId
  };
}

function getCognitoClient(region) {
  if (!cachedCognitoClient || cachedCognitoRegion !== region) {
    cachedCognitoClient = new CognitoIdentityProviderClient({ region });
    cachedCognitoRegion = region;
  }

  return cachedCognitoClient;
}

function normalizeSession(rawSession) {
  if (!rawSession || typeof rawSession !== "object") {
    return undefined;
  }

  const accessToken = typeof rawSession.accessToken === "string" ? rawSession.accessToken.trim() : "";
  const idToken = typeof rawSession.idToken === "string" ? rawSession.idToken.trim() : "";
  const refreshToken = typeof rawSession.refreshToken === "string" ? rawSession.refreshToken.trim() : "";
  const expiresAt = Number(rawSession.expiresAt);
  const username = typeof rawSession.username === "string" ? rawSession.username.trim() : "";

  if (!accessToken || !Number.isFinite(expiresAt) || expiresAt <= 0) {
    return undefined;
  }

  return {
    accessToken,
    idToken,
    refreshToken,
    expiresAt,
    username
  };
}

function parseStoredSession() {
  if (typeof window === "undefined") {
    return undefined;
  }

  const raw = window.localStorage.getItem(COGNITO_SESSION_STORAGE_KEY);
  if (!raw) {
    return undefined;
  }

  try {
    return normalizeSession(JSON.parse(raw));
  } catch {
    return undefined;
  }
}

function serializeSession(session) {
  return JSON.stringify({
    accessToken: session.accessToken,
    idToken: session.idToken,
    refreshToken: session.refreshToken,
    expiresAt: session.expiresAt,
    username: session.username
  });
}

function buildSessionFromAuthResult(authResult, fallback) {
  const accessToken = typeof authResult?.AccessToken === "string" ? authResult.AccessToken.trim() : "";
  const idToken = typeof authResult?.IdToken === "string" ? authResult.IdToken.trim() : fallback?.idToken || "";
  const refreshToken =
    typeof authResult?.RefreshToken === "string" ? authResult.RefreshToken.trim() : fallback?.refreshToken || "";
  const expiresIn = Number(authResult?.ExpiresIn);

  if (!accessToken) {
    throw new Error("Cognito non ha restituito un access token.");
  }

  const ttlSeconds = Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 3600;
  return {
    accessToken,
    idToken,
    refreshToken,
    username: fallback?.username || "",
    expiresAt: Date.now() + ttlSeconds * 1000
  };
}

export function isCognitoAdminAuthMode() {
  return ADMIN_AUTH_MODE === "cognito";
}

export function isCognitoAdminAuthConfigured() {
  return Boolean(getCognitoConfig());
}

export function readStoredCognitoSession() {
  return parseStoredSession();
}

export function persistCognitoSession(session) {
  if (typeof window === "undefined") {
    return;
  }

  if (!session) {
    window.localStorage.removeItem(COGNITO_SESSION_STORAGE_KEY);
    return;
  }

  const normalized = normalizeSession(session);
  if (!normalized) {
    window.localStorage.removeItem(COGNITO_SESSION_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(COGNITO_SESSION_STORAGE_KEY, serializeSession(normalized));
}

export function clearCognitoSession() {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(COGNITO_SESSION_STORAGE_KEY);
}

export function isCognitoSessionExpired(session) {
  if (!session || typeof session.expiresAt !== "number") {
    return true;
  }
  return Date.now() >= session.expiresAt - SESSION_EXPIRY_SKEW_MS;
}

export function getCognitoSessionAccessToken(session) {
  if (!session || isCognitoSessionExpired(session)) {
    return "";
  }
  return session.accessToken || "";
}

function mapCognitoSignInError(error) {
  const code = String(error?.name || "");
  const message = String(error?.message || "");

  if (code === "NotAuthorizedException") {
    return "Credenziali non valide.";
  }

  if (code === "UserNotFoundException") {
    return "Utente admin non trovato.";
  }

  if (code === "PasswordResetRequiredException") {
    return "Password da reimpostare: completa il reset utente su Cognito.";
  }

  if (message.toLowerCase().includes("new_password_required")) {
    return "Utente con password temporanea: imposta prima una password permanente su Cognito.";
  }

  return "Login admin non riuscito.";
}

export async function signInWithCognito(credentials) {
  const config = getCognitoConfig();
  if (!config) {
    throw new Error("Configurazione Cognito admin mancante nel frontend.");
  }

  const username = String(credentials?.username || "").trim().toLowerCase();
  const password = String(credentials?.password || "");
  if (!username || !password) {
    throw new Error("Inserisci email e password.");
  }

  const cognitoClient = getCognitoClient(config.region);

  try {
    const command = new InitiateAuthCommand({
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: config.clientId,
      AuthParameters: {
        USERNAME: username,
        PASSWORD: password
      }
    });
    const response = await cognitoClient.send(command);
    if (response.ChallengeName) {
      throw new Error(`Challenge non supportata: ${response.ChallengeName}`);
    }

    return buildSessionFromAuthResult(response.AuthenticationResult, { username });
  } catch (error) {
    throw new Error(mapCognitoSignInError(error));
  }
}

export async function refreshCognitoSession(session) {
  const config = getCognitoConfig();
  if (!config) {
    throw new Error("Configurazione Cognito admin mancante nel frontend.");
  }

  const refreshToken = typeof session?.refreshToken === "string" ? session.refreshToken.trim() : "";
  if (!refreshToken) {
    throw new Error("Refresh token assente.");
  }

  const cognitoClient = getCognitoClient(config.region);
  const command = new InitiateAuthCommand({
    AuthFlow: "REFRESH_TOKEN_AUTH",
    ClientId: config.clientId,
    AuthParameters: {
      REFRESH_TOKEN: refreshToken
    }
  });

  try {
    const response = await cognitoClient.send(command);
    return buildSessionFromAuthResult(response.AuthenticationResult, session);
  } catch (error) {
    throw new Error(mapCognitoSignInError(error));
  }
}
